import {
  SvgIcon,
} from '@mui/material';
import {IconEmail, IconSMS} from '@zoom/sun-ui-icon';
import {formatDuration} from 'date-fns';

import apptNotifEmailFollowUp from './templates/apptNotificationEmailFollowUp.json';
import apptNotifEmailReminder from './templates/apptNotificationEmailReminder.json';
import apptNotifSmsReminder from './templates/apptNotificationSmsReminder.json';
import workflowBookingEmail from './templates/workflowBookingEmail.json';
import workflowCalendarInvite from './templates/workflowCalendarInvite.json';
import workflowCancellationEmail from './templates/workflowCancellationEmail.json';
import workflowRescheduleEmail from './templates/workflowRescheduleEmail.json';

import {ReactComponent as BellOutlined} from 'assets/svg/bell-outlined.svg';
import {ReactComponent as CancellationOutlined} from 'assets/svg/calendar-cancellation-outlined.svg';
import {ReactComponent as ConfirmationOutlined} from 'assets/svg/calendar-confirmation-outlined.svg';
import {ReactComponent as ReconfirmationOutlined} from 'assets/svg/calendar-reconfirmation-outlined.svg';
import {ReactComponent as RescheduleOutlined} from 'assets/svg/calendar-reschedule-outlined.svg';
import {ReactComponent as FileFormatOutlined} from 'assets/svg/file-format-outlined.svg';
import {ReactComponent as HeartOutlined} from 'assets/svg/heart-outlined.svg';
import {ReactComponent as Plus} from 'assets/svg/plus.svg';
import {ReactComponent as PollOutlined} from 'assets/svg/poll-outlined.svg';
import {ReactComponent as PuzzleOutlined} from 'assets/svg/puzzle-outlined.svg';
import {ReactComponent as TeamChatOutlined} from 'assets/svg/team-chat-outlined.svg';
import {getCurrentLocale} from 'i18n';
import {strEq} from 'Utils';
import {
  DAY_MINUTES,
  DEFAULT_WORKFLOW_TEMPLATE_BODY,
  HOUR_MINUTES,
  WF_ACTION_RECEIVER,
  WF_METHOD,
  WF_TRIGGER_TYPE,
  WF_TYPE,
} from 'Utils/consts';

export const SYSTEM_WORKFLOWS = [
  workflowBookingEmail,
  workflowRescheduleEmail,
  workflowCancellationEmail,
];

export const SYSTEM_EMAIL_WORKFLOWS = [
  workflowBookingEmail,
  // TODO pending server
  // workflowRescheduleEmail,
  workflowCancellationEmail,
];

export const SYSTEM_CALENDAR_WORKFLOWS = [
  workflowCalendarInvite,
];


export const SYSTEM_INDIVIDUAL_WORKFLOWS = [
  apptNotifEmailReminder,
  ...(process.env.REACT_APP_ENABLE_SMS === 'true')? [apptNotifSmsReminder]: [],
  apptNotifEmailFollowUp,
];

// UI-only calendar message template const
export const CALENDAR_TEMPLATE = {
  'body': DEFAULT_WORKFLOW_TEMPLATE_BODY,
  'subject': '{{event_name}} - {{attendee_full_name}} and {{organizer_name}}',
  'method': WF_METHOD.CALENDAR,
  'receiver': WF_ACTION_RECEIVER.BOOKER,
  'variant': WF_TYPE.CALENDAR_INVITE,
};

// answer1...answer10, available in any place
const ANSWER_VARIABLES = Array.from({length: 10}, (_, i)=> i+1).map((i)=> `answer${i}`);

export const EMAIL_SUBJ_VARIABLES = [
  'attendee_count',
  'attendee_email',
  'attendee_first_name',
  'attendee_full_name',
  'attendee_last_name',
  'attendee_phone_number',
  'coming_time',
  'event_date',
  'event_duration',
  'event_name',
  'event_time',
  'organizer_email',
  'organizer_name',
  'seat_count',
  ...ANSWER_VARIABLES,
];

export const EMAIL_VARIABLES = [
  'attendee_count',
  'attendee_email',
  'attendee_first_name',
  'attendee_full_name',
  'attendee_last_name',
  'attendee_phone_number',
  'coming_time',
  'event_date',
  'event_duration',
  'description',
  'event_location',
  'event_name',
  'event_time',
  'organizer_email',
  'organizer_name',
  'questions_and_answers',
  'seat_count',
  ...ANSWER_VARIABLES,
];

// Supported template variables for calendar events
export const CALENDAR_EVENT_SUBJ_VARIABLES = [
  'organizer_name',
  'attendee_full_name',
  'attendee_first_name',
  'attendee_last_name',
  'event_name',
  ...ANSWER_VARIABLES,
];

export const CALENDAR_EVENT_BODY_VARIABLES = [
  'organizer_name',
  'attendee_full_name',
  'attendee_first_name',
  'attendee_last_name',
  'event_name',
  'questions_and_answers',
  'event_location',
  'description',
  ...ANSWER_VARIABLES,
];


export const WF_VARIABLE_CHARS_ESTIMATE = {
  'attendee_count': 2,
  'attendee_email': 23,
  'attendee_first_name': 6,
  'attendee_last_name': 7,
  'attendee_full_name': 13,
  'attendee_phone_number': 15,
  'event_name': 24,
  'event_date': 17,
  'event_time': 30,
  'event_location': 40,
  'event_duration': 10,
  'organizer_email': 23,
  'organizer_name': 13,
  'seat_count': 10,
  'coming_time': 20,
  // estimate answers1...answer10 to 30 characters
  ...(ANSWER_VARIABLES.reduce((res, item) => {
    res[item] = 30;
    return res;
  }, {})),
};

const defaultVariants = {
  [WF_TYPE.CONFIRMATION]: true,
  [WF_TYPE.RESCHEDULE]: true,
  [WF_TYPE.CANCELLATION]: true,
};

const beforeEventVariants = {
  [WF_TYPE.RECONFIRMATION]: true,
  [WF_TYPE.REMINDER]: true,
};

const afterEventVariants = {
  [WF_TYPE.REQUEST_FOLLOW_UP]: true,
  [WF_TYPE.FEEDBACK_SURVEY]: true,
  [WF_TYPE.THANK_YOU]: true,
};

const otherVariants = {
  [WF_TYPE.ADDL_RESOURCE]: true,
};

const customVariants = {
  [WF_TYPE.CUSTOM]: true,
};

const cciVariants = {
  [WF_TYPE.CCI]: true,
};

export const isDefaultWorkflow = (workflow) => !!workflow?.isSystem;

/**
 * @param {Workflow} workflow
 * @return {boolean}
 */
export const isSmsOnlyWorkflow = (workflow) => {
  if (isDefaultWorkflow(workflow)) {
    return false;
  }
  if (workflow?.type === 'attendeeSmsReminder') {
    return true;
  }
  return !!defaultVariants[workflow?.type];
};

/**
 *
 * @param {Workflow} workflow
 * @return {boolean}
 */
export const isEmailOnlyWorkflow = (workflow) => {
  if (workflow?.type === 'attendeeEmailReminder' || workflow?.type === 'attendeeEmailFollowUp') {
    return true;
  }
  return (!workflow.text || workflow.text?.length === 0);
};

/**
 * @param {Workflow} workflow
 * @param {{email: boolean, text: boolean}} enabledNotifMethods
 * @return {boolean}
 */
export const isAdminDisabled = (workflow, enabledNotifMethods) => {
  if (workflow.isSystem) {
    // System notifications are necessary for functionality, can never be disabled
    return false;
  }
  const adminDisabledEmail = !enabledNotifMethods.email;
  const adminDisabledText = !enabledNotifMethods.text;
  if (adminDisabledEmail && adminDisabledText) {
    return true;
  }
  if (isSmsOnlyWorkflow(workflow) && adminDisabledText) {
    return true;
  }
  if (isEmailOnlyWorkflow(workflow) && adminDisabledEmail) {
    return true;
  }
  return false;
};

export const isBeforeEventWorkflow = (workflow) => !!beforeEventVariants[workflow?.type];

export const isAfterEventWorkflow = (workflow) => !!afterEventVariants[workflow?.type];

export const isOtherEventWorkflow = (workflow) => !!otherVariants[workflow?.type];

export const isCciEventWorkflow = (workflow) => !!cciVariants[workflow?.type];

export const isCustomEventWorkflow = (workflow) => {
  const nonCustomWorkflowChecks = [
    isDefaultWorkflow,
    isSmsOnlyWorkflow,
    isBeforeEventWorkflow,
    isAfterEventWorkflow,
    isOtherEventWorkflow,
    isCciEventWorkflow,
  ];
  for (const nonCustomWorkflowCheck of nonCustomWorkflowChecks) {
    if (nonCustomWorkflowCheck(workflow)) {
      return false;
    }
  }
  if (customVariants[workflow.type]) {
    return true;
  }
  console.error('unrecognized notification type');
  return true;
};

const iconMap = {
  [WF_TYPE.CREATE_NEW]: Plus,
  [WF_TYPE.CONFIRMATION]: ConfirmationOutlined,
  [WF_TYPE.RESCHEDULE]: RescheduleOutlined,
  [WF_TYPE.CANCELLATION]: CancellationOutlined,
  [WF_TYPE.RECONFIRMATION]: ReconfirmationOutlined,
  [WF_TYPE.REMINDER]: BellOutlined,
  [WF_TYPE.THANK_YOU]: HeartOutlined,
  [WF_TYPE.FEEDBACK_SURVEY]: PollOutlined,
  [WF_TYPE.REQUEST_FOLLOW_UP]: TeamChatOutlined,
  [WF_TYPE.ADDL_RESOURCE]: FileFormatOutlined,
  [WF_TYPE.CUSTOM]: PuzzleOutlined,
  [WF_TYPE.APPT_EMAIL_REMINDER]: IconEmail,
  [WF_TYPE.APPT_SMS_REMINDER]: IconSMS,
  [WF_TYPE.APPT_EMAIL_FOLLOW_UP]: IconEmail,
};
export const getWorkflowTypeIcon = (type) => {
  const workflowIcon = iconMap[type] || PuzzleOutlined;
  return <SvgIcon fontSize="inherit" component={workflowIcon} inheritViewBox />;
};

const systemWorkflowNameMap = {
  [WF_TYPE.CONFIRMATION]: 'landing.bookingConfirmationEmail',
  [WF_TYPE.RESCHEDULE]: 'landing.rescheduleEmail',
  [WF_TYPE.CANCELLATION]: 'landing.cancellationEmail',
  [WF_TYPE.CALENDAR_INVITE]: 'landing.calendarInvite',
};
const workflowNameMap = {
  [WF_TYPE.APPT_EMAIL_REMINDER]: 'landing.reminderEmail',
  [WF_TYPE.APPT_SMS_REMINDER]: 'landing.reminderSMS',
  [WF_TYPE.APPT_EMAIL_FOLLOW_UP]: 'landing.followUpEmail',
  [WF_TYPE.CREATE_NEW]: 'landing.createCustomNotification',
  [WF_TYPE.CONFIRMATION]: 'landing.bookingConfirmationSMS',
  [WF_TYPE.RESCHEDULE]: 'landing.rescheduleSMS',
  [WF_TYPE.CANCELLATION]: 'landing.cancellationSMS',
  [WF_TYPE.RECONFIRMATION]: process.env.REACT_APP_ENABLE_SMS === 'true' ?
    'landing.reconfirmationEmailSMS' :
    'landing.reconfirmationEmail',
  [WF_TYPE.REMINDER]: process.env.REACT_APP_ENABLE_SMS === 'true' ?
    'landing.reminderEmailSMS' :
    'landing.reminderEmail',
  [WF_TYPE.THANK_YOU]: 'landing.thankYouEmail',
  [WF_TYPE.FEEDBACK_SURVEY]: 'landing.quickSurveyEmail',
  [WF_TYPE.REQUEST_FOLLOW_UP]: process.env.REACT_APP_ENABLE_SMS === 'true' ?
    'landing.followUpEmailSMS' :
    'landing.followUpEmail',
  [WF_TYPE.ADDL_RESOURCE]: 'landing.additionalResourcesEmail',
};
export const getWorkflowName = (t, workflow) => {
  if (workflow?.isSystem) {
    return t(systemWorkflowNameMap[workflow.type]);
  }
  if (Object.keys(workflowNameMap).includes(workflow?.type)) {
    return t(workflowNameMap[workflow.type]);
  }
  if (!workflow?.type || workflow?.type === 'custom') {
    return workflow?.name;
  }
  console.error('Unexpected workflow type');
  return workflow?.name;
};

export const workflowHasHostSMS = (workflow) => {
  const workflowHasHostSMS = (workflow?.text || []).some((smsItem) => smsItem.receiver !== WF_ACTION_RECEIVER.BOOKER);
  return workflowHasHostSMS;
};

export const workflowActive = (workflow) => {
  return workflow?.appointmentId?.length > 0;
};

export const convertMinutes = (minutes, t) => {
  if (minutes === 0) {
    return t('landing.immediately');
  }

  const absMinutes = Math.abs(minutes);

  let days = 0;
  let hours = 0;
  let min = 0;
  days = Math.floor(absMinutes / DAY_MINUTES);
  hours = Math.floor((absMinutes % DAY_MINUTES) / HOUR_MINUTES);
  min = (absMinutes % HOUR_MINUTES);
  return formatDuration(
    {
      days,
      hours,
      minutes: min,
    },
    {
      locale: getCurrentLocale(),
    });
};

export const renderTriggerSummary = (min, type, t) => {
  if (!type) {
    return '';
  }
  let minutesArr = min;
  if (!Array.isArray(min)) {
    minutesArr = [min];
  }
  return minutesArr.map((minutes) => {
    const duration = convertMinutes(minutes, t);
    if (minutes === 0 && type === WF_TRIGGER_TYPE.EVENT_START) {
      return t('landing.whenEventStarts');
    } else if (type === WF_TRIGGER_TYPE.EVENT_START) {
      if (minutes <= 0) {
        return t('landing.durationAfterEventStarts', {duration, count: minutes});
      }
      return t('landing.durationBeforeEvent', {duration, count: minutes});
    } else if (type === WF_TRIGGER_TYPE.EVENT_END) {
      return t('landing.durationAfterEventEnds', {duration, count: minutes});
    } else if (type === WF_TRIGGER_TYPE.EVENT_BOOKED) {
      return t('landing.durationAfterBooking', {duration, count: minutes});
    } else if (type === WF_TRIGGER_TYPE.EVENT_CANCEL) {
      return t('landing.durationAfterCancelling', {duration, count: minutes});
    } else {
      console.log('unrecognized trigger type');
      return null;
    }
  }).filter((x) => x).join(', ');
};

export const renderActionSummary = (method, action) => {
  let medium = 'Email';
  if (method === WF_METHOD) {
    medium = 'Text';
  }

  return `${medium} the ${action.receiver}`;
};

// Gets the form state values from the stored server workflow model
// server workflow stores trigger time in minutes before the event
// {minutes: -15, trigger: 'end'} => 15 minutes after event end
export const modelToFormTrigger = (wf) => {
  let triggerType = wf?.trigger || WF_TRIGGER_TYPE.EVENT_END;
  let minutes = wf?.minutes === undefined ? -15 : wf?.minutes;
  // if the server trigger minutes are negative, it is intended to trigger AFTER event start.
  if (wf?.minutes >= 0) {
    if (wf?.trigger === WF_TRIGGER_TYPE.EVENT_START ||
      // In appt level notifications, minutes is an array, trigger is undefined
      (wf?.minutes?.length && wf?.trigger === undefined)) {
      triggerType = WF_TRIGGER_TYPE.EVENT_BEFORE_START;
    }
  }
  // form state always store minutes as positive
  minutes = Math.abs(minutes);

  const days = minutes % (DAY_MINUTES);
  const hours = minutes % 60;

  let durationUnit = 1;
  if (days === 0 && minutes !== 0) {
    durationUnit = DAY_MINUTES;
  } else if (hours === 0 && minutes !== 0) {
    durationUnit = HOUR_MINUTES;
  } else {
    durationUnit = 1;
  }
  return {
    type: triggerType,
    durationUnit: durationUnit,
    durationAmount: minutes / durationUnit,
  };
};

// Creates the server workflow trigger payload fields from the form state values
export const formTriggerToModel = (formTrigger) => {
  if (!formTrigger) {
    return null;
  }
  const isBeforeStart = (formTrigger.type === WF_TRIGGER_TYPE.EVENT_BEFORE_START);
  // Want to represent the "1 hour before event start" trigger as { minutes: 60, type: 'start' }
  const useNegative = (isBeforeStart) ? 1 : -1;
  const triggerType = (isBeforeStart) ? WF_TRIGGER_TYPE.EVENT_START : formTrigger.type;
  return {
    trigger: triggerType,
    minutes: formTrigger.durationAmount * formTrigger.durationUnit * useNegative,
  };
};

export const WF_DEFAULT_LANG = 'en-US';

export const WF_SUPPORTED_LANGUAGES = Object.freeze([
  'en-US',
  'es-ES',
  'de-DE',
  'zh-CN',
  'zh-TW',
  'fr-FR',
  'pt-BR',
  'ja-JP',
  'ko-KR',
  'it-IT',
  'nl-NL',
]);

/**
 * Depending on the given locale value, returns a workflow-supported locale value or falls back to en-US.
 * @param {string} localeVal
 * @return {string}
*/
export const getWfLanguage = (localeVal) => {
  const remapWebLocales = {
    'jp-JP': 'ja-JP', // Remap web profile jp-JP to ja-JP
    'pt-PT': 'pt-BR', // Remap European Portugues web profile pt-PT to pt-BR
    'ko-KO': 'ko-KR', // Remap Korean web profile ko-KO to ko-KR
  };
  const localeCode = remapWebLocales[localeVal] ?? localeVal;
  return WF_SUPPORTED_LANGUAGES.find((language) => strEq(language, localeCode)) ?? WF_DEFAULT_LANG;
};
